/**
 * Shared rules for "is the person sending this parcel the account owner?".
 *
 * The same comparison runs on the server (utils/senderIdentity.js in
 * qcexpress-be) — the client copy exists to show the upload field at the right
 * moment, not to be the gate. Keep the two in step.
 */

/**
 * The same human's phone reaches us as 08031234567 from the profile and
 * +2348031234567 from the booking form. Comparing the last 10 significant
 * digits makes those equal without a country-code table.
 */
export const normalizePhone = (phone: unknown): string => {
  const digits = String(phone ?? '').replace(/\D/g, '')
  return digits.length > 10 ? digits.slice(-10) : digits
}

const nameTokens = (name: unknown): string[] =>
  String(name ?? '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)

/**
 * True when every token of one name appears in the other — tolerates a middle
 * name on one side ("John Doe" vs "John Ade Doe") and word order, but not a
 * genuinely different person.
 */
export const namesMatch = (a: unknown, b: unknown): boolean => {
  const left = nameTokens(a)
  const right = nameTokens(b)
  if (!left.length || !right.length) return false
  const [short, long] =
    left.length <= right.length ? [left, right] : [right, left]
  return short.every((token) => long.includes(token))
}

/**
 * The sender-related slice of the `delivery` state the booking forms carry.
 * The index signature keeps the rest of that (large, untyped) object assignable.
 */
export type SenderDelivery = {
  sender_first_name?: string
  sender_last_name?: string
  pickup_number?: string
  senderIsAccountOwner?: boolean
  /** Private reference (`cld-private:…`) or a legacy public URL — submitted. */
  senderIdImage?: string
  /** Signed, expiring display link — never submitted. */
  senderIdImageUrl?: string | null
  [key: string]: unknown
}

export type SenderProfile = {
  firstname?: string
  lastname?: string
  phonenumber?: string
  [key: string]: unknown
}

export const senderMatchesAccountOwner = (
  delivery: SenderDelivery,
  profile: SenderProfile
): boolean => {
  const ownerPhone = normalizePhone(profile?.phonenumber)
  if (!ownerPhone) return false
  return (
    namesMatch(
      `${delivery?.sender_first_name || ''} ${delivery?.sender_last_name || ''}`,
      `${profile?.firstname || ''} ${profile?.lastname || ''}`
    ) && normalizePhone(delivery?.pickup_number) === ownerPhone
  )
}

/**
 * Delivery types that enforce the rule. Mirrors SENDER_ID_DELIVERY_TYPES in
 * utils/senderIdentity.js on the server — keep the two in step.
 *
 * IMPORT is deliberately absent: on an import the sender is the party abroad
 * and the account owner is the receiver, so the rule would fire on essentially
 * every import and demand a government ID from a foreign supplier who is not
 * our customer.
 */
export const SENDER_ID_DELIVERY_TYPES = ['DOMESTIC', 'EXPORT']

export const senderIdEnforced = (deliveryType?: string): boolean =>
  SENDER_ID_DELIVERY_TYPES.includes(String(deliveryType || '').toUpperCase())

/**
 * Whether the sender is somebody other than the account owner — because the
 * customer said so explicitly, or because the name/phone they typed simply is
 * not the account owner's. Independent of whether we enforce an ID for it.
 */
export const senderIsThirdParty = (
  delivery: SenderDelivery,
  profile: SenderProfile
): boolean =>
  delivery?.senderIsAccountOwner === false ||
  !senderMatchesAccountOwner(delivery, profile)

/** True when this flow must collect the sender's ID before continuing. */
export const senderIdRequired = (
  delivery: SenderDelivery,
  profile: SenderProfile,
  deliveryType?: string
): boolean =>
  senderIdEnforced(deliveryType) &&
  senderFieldsComplete(delivery) &&
  senderIsThirdParty(delivery, profile)

/**
 * Auto-detect only fires once the sender fields are filled in — otherwise the
 * upload box appears on an empty form before the customer has typed anything.
 */
export const senderFieldsComplete = (delivery: SenderDelivery): boolean =>
  Boolean(
    delivery?.sender_first_name &&
      delivery?.sender_last_name &&
      delivery?.pickup_number
  )

/** Null when fine, otherwise the message to show the customer. */
export const validateSenderId = (
  delivery: SenderDelivery,
  profile: SenderProfile,
  deliveryType?: string
): string | null => {
  if (!senderIdRequired(delivery, profile, deliveryType)) return null
  if (delivery?.senderIdImage) return null
  return delivery?.senderIsAccountOwner === false
    ? "You indicated that someone other than the account owner is sending this shipment — upload a photo of that sender's government-issued ID to continue"
    : "The sender's name and phone number do not match the account owner — upload a photo of the sender's government-issued ID to continue"
}

/** The two fields every booking/draft payload should carry. */
export const senderIdentityPayload = (
  delivery: SenderDelivery,
  profile: SenderProfile
) => ({
  senderIsAccountOwner: !senderIsThirdParty(delivery, profile),
  senderIdImage: delivery?.senderIdImage || '',
  // Display-only; drafts spread the whole `delivery` before this, so blank it
  // here (undefined is dropped when the payload is JSON-encoded).
  senderIdImageUrl: undefined,
})
